// Copyright (c) 2021-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import {
    NavLink,
    useLocation,
} from "react-router-dom";
import {
    COOKIES_ROUTE,
    INDEX_ROUTE,
    MY_CLIENT_LIST_ROUTE,
} from "../../../../core/constants/route";
import { PalvelinkauppaLogo } from "../../../assets/logos";
import {
    ACTIVATED_CLASS_NAME,
    APP_HEADER_CLASS_NAME,
    NAV_LINK_CLASS_NAME,
} from "../../../../core/constants/className";
import { useMenuItems } from "../../../hooks/useMenuItems";
import { CookieConsentView } from "../../views/cookieConsentView/CookieConsentView";
import { MobileMenuToggle } from "../mobileMenuToggle/MobileMenuToggle";
import { NavMenu } from "../navMenu/NavMenu";
import { SetThemeCallback } from "../../../../../../io/hyperify/frontend/hooks/useTheme";
import { Theme } from "../../../../../../io/hyperify/core/types/Theme";
import { PhoneLink } from "../PhoneLink";
import { COMPANY_PHONE_NUMBER_DISPLAY } from "../../../../core/constants/company";
import { ShoppingCartIcon, SignInIcon } from "../../../assets/icons";
import { useCallback, MouseEvent, useRef } from "react";
import { CartArticle } from "../cart/CartArticle";
import { useShoppingCartMenu } from "../../../../../../io/hyperify/frontend/hooks/useShoppingCartMenu";
import { useDropdownToggle } from "../../../../../../io/hyperify/frontend/hooks/useDropdownToggle";
import { useShoppingCart } from "../../../../../../io/hyperify/frontend/hooks/useShoppingCart";
import { useCartUpdatedEvent } from "../../../hooks/useCartUpdatedEvent";
import { LogService } from "../../../../../../io/hyperify/core/LogService";
import { TranslationFunction } from "../../../../../../io/hyperify/core/types/TranslationFunction";
import "./AppHeader.scss";

const LOG = LogService.createLogger('AppHeader');

export interface AppHeaderProps {
    readonly t: TranslationFunction;
    readonly className?: string;
    readonly theme?: Theme;
    readonly changeTheme: SetThemeCallback;
    readonly isFixed?: boolean;
}

export function AppHeader (props: AppHeaderProps) {

    const MENU_ITEMS = useMenuItems();
    const cartRef = useRef<HTMLDivElement>(null);
    const isFixed = props?.isFixed ?? false;
    const t = props?.t;
    const [ isNavOpen, toggleNav, closeNavMenu ] = useDropdownToggle('AppHeader', false);
    const [ isCartOpen, toggleCartMenu, closeCartMenu ] = useShoppingCartMenu('AppHeader', cartRef);
    const [ cart, refreshCart ] = useShoppingCart();

    const myRefreshCartCallback = useCallback(
        () => {
            LOG.debug(`Cart updated`);
            refreshCart();
        },
        [
            refreshCart
        ]
    );

    const onToggleCart = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            if ( event ) {
                event.preventDefault();
                event.stopPropagation();
            }
            toggleCartMenu();
            closeNavMenu();
        },
        [
            toggleCartMenu,
            closeNavMenu
        ]
    );

    const toggleNavCallback = useCallback(
        () => {
            toggleNav();
            closeCartMenu();
        },
        [
            toggleNav,
            closeCartMenu
        ]
    );

    const location = useLocation();
    const path = location.pathname;

    useCartUpdatedEvent(myRefreshCartCallback);

    return (
        <div className={
            APP_HEADER_CLASS_NAME
            + " " + (isFixed ? "enable-fixed" : "disable-fixed")
        }>

            {path !== COOKIES_ROUTE ? (
                <CookieConsentView t={t} enableSettingsButton={true} />
            ) : null}

            <header className={APP_HEADER_CLASS_NAME + "-nav"}>

                <nav className={`${APP_HEADER_CLASS_NAME}-menu-normal`}>

                    <NavLink
                        to={INDEX_ROUTE}
                        className={({isActive}) =>
                            NAV_LINK_CLASS_NAME + " " +
                            `${APP_HEADER_CLASS_NAME}-logo` +
                            (isActive ? " " + ACTIVATED_CLASS_NAME : "")
                        }
                    ><PalvelinkauppaLogo /></NavLink>

                    <NavMenu
                        t={t}
                        className={APP_HEADER_CLASS_NAME + "-nav-menu"}
                        items={MENU_ITEMS}
                    />

                    <div className="container--toggle">

                        <div className="phone-button">
                            <PhoneLink phone={COMPANY_PHONE_NUMBER_DISPLAY} />
                        </div>

                        <NavLink
                            to={MY_CLIENT_LIST_ROUTE}
                            className={"login-button button"}
                        ><SignInIcon /></NavLink>

                        <button
                            onClick={onToggleCart}
                            className={"cart-button button"}
                        ><ShoppingCartIcon /></button>

                        {/*<input*/}
                        {/*    type="checkbox"*/}
                        {/*    id={`mobile-toggle`}*/}
                        {/*    className="toggle--checkbox"*/}
                        {/*/>*/}
                        {/*<label*/}
                        {/*    htmlFor={`mobile-toggle`}*/}
                        {/*    className="toggle--label"*/}
                        {/*>*/}
                        {/*  <span className="toggle--label-background">&#9788; </span>*/}
                        {/*  <span className="toggle--label-moon-background">&#9790;</span>*/}
                        {/*</label>*/}
                    </div>

                </nav>

                <div className={APP_HEADER_CLASS_NAME + "-actions-container"}>

                    <div className={"mobile-menu-toggle-container"}>

                        <NavLink
                            to={INDEX_ROUTE}
                            className={({isActive}) =>
                                NAV_LINK_CLASS_NAME + " " +
                                `${APP_HEADER_CLASS_NAME}-logo` +
                                (isActive ? " " + ACTIVATED_CLASS_NAME : "")
                            }
                        ><PalvelinkauppaLogo /></NavLink>

                        <div className="container--toggle">

                            <MobileMenuToggle
                                isThisOpen={isNavOpen}
                                toggleMenu={toggleNavCallback}
                            />

                            <NavLink
                                to={MY_CLIENT_LIST_ROUTE}
                                className={"login-button button"}
                            ><SignInIcon /></NavLink>

                            <button
                                onClick={onToggleCart}
                                className={"cart-button button"}
                            ><ShoppingCartIcon /></button>

                            {/*<input*/}
                            {/*    type="checkbox"*/}
                            {/*    id={`mobile-toggle`}*/}
                            {/*    className="toggle--checkbox"*/}
                            {/*/>*/}
                            {/*<label*/}
                            {/*    htmlFor={`mobile-toggle`}*/}
                            {/*    className="toggle--label"*/}
                            {/*>*/}
                            {/*  <span className="toggle--label-background">&#9788; </span>*/}
                            {/*  <span className="toggle--label-moon-background">&#9790;</span>*/}
                            {/*</label>*/}
                        </div>

                    </div>

                    {isNavOpen ? (
                        <NavMenu
                            t={t}
                            className={APP_HEADER_CLASS_NAME + "-nav-menu"}
                            items={MENU_ITEMS}
                        />
                    ) : null}

                </div>
            </header>

            {isCartOpen ? (
                <div ref={cartRef} className={APP_HEADER_CLASS_NAME + "-cart-container"}>
                    <CartArticle
                        t={t}
                        className={"cart"}
                        cart={cart}
                    />
                </div>
            ) : null}

        </div>
    );

}
